import { useEffect } from 'react';
import { Crown, Users, Scale, Globe, BookOpen, Heart } from 'lucide-react';

const Tinorangatiratanga = () => {
  useEffect(() => {
    document.title = 'Tino Rangatiratanga | He Whakaputanga nga Hapū o te Tairawhiti';
    return () => {
      document.title = 'He Whakaputanga nga Hapū o te Tairawhiti | Customary Rights Platform';
    };
  }, []);

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Header Section */}
      <section className="bg-gradient-to-r from-ocean-teal to-ocean-deep text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Tino Rangatiratanga
          </h1>
          <p className="text-xl md:text-2xl max-w-4xl mx-auto opacity-90">
            Self-determination, sovereignty, and the right to govern according to tikanga Māori
          </p>
        </div>
      </section>

      {/* Definition Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h2 className="section-header">What is Tino Rangatiratanga?</h2>
            <div className="card bg-ocean-blue bg-opacity-10">
              <p className="text-lg text-gray-700 leading-relaxed">
                <strong>Tino Rangatiratanga</strong> translates to "absolute sovereignty" or "self-determination." 
                It represents the right of Māori to exercise authority over their own affairs, 
                including political, economic, social, and cultural matters, according to their own values and systems.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="card text-center">
              <Crown className="h-12 w-12 text-ocean-teal mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-3 text-gray-800">Sovereignty</h3>
              <p className="text-gray-600">
                The inherent right to self-governance and control over traditional territories and resources.
              </p>
            </div>

            <div className="card text-center">
              <Users className="h-12 w-12 text-ocean-teal mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-3 text-gray-800">Self-Determination</h3>
              <p className="text-gray-600">
                The right to determine political status and pursue economic, social, and cultural development.
              </p>
            </div>

            <div className="card text-center">
              <Scale className="h-12 w-12 text-ocean-teal mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-3 text-gray-800">Justice</h3>
              <p className="text-gray-600">
                The right to maintain and develop distinct legal and justice systems based on tikanga.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Historical Context */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="section-header text-center mb-12">Historical Context</h2>
          
          <div className="max-w-4xl mx-auto">
            <div className="timeline space-y-8">
              <div className="cultural-border">
                <h3 className="text-xl font-semibold mb-3 text-ocean-teal">Pre-1840: Traditional Sovereignty</h3>
                <p className="text-gray-600">
                  Māori exercised tino rangatiratanga through iwi and hapū structures, 
                  with rangatira (chiefs) holding authority over their territories and people.
                </p>
              </div>

              <div className="cultural-border">
                <h3 className="text-xl font-semibold mb-3 text-ocean-teal">1835: He Whakaputanga</h3>
                <p className="text-gray-600">
                  The Declaration of Independence affirmed Māori sovereignty and the collective authority 
                  of rangatira over their lands and people.
                </p>
              </div>

              <div className="cultural-border">
                <h3 className="text-xl font-semibold mb-3 text-ocean-teal">1840: Te Tiriti o Waitangi</h3>
                <p className="text-gray-600">
                  The Māori text of the Treaty guaranteed tino rangatiratanga over lands, villages, 
                  and treasures, while the English version spoke of "full exclusive and undisturbed possession."
                </p>
              </div>

              <div className="cultural-border">
                <h3 className="text-xl font-semibold mb-3 text-ocean-teal">Modern Era: Ongoing Assertion</h3>
                <p className="text-gray-600">
                  Contemporary Māori continue to assert tino rangatiratanga through various means, 
                  including the Māori sovereignty movement and constitutional discussions.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Modern Expression */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="section-header text-center mb-12">Modern Expression of Tino Rangatiratanga</h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="card">
              <Globe className="h-8 w-8 text-ocean-teal mb-4" />
              <h3 className="text-xl font-semibold mb-3 text-gray-800">Political Self-Determination</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• Māori electoral representation</li>
                <li>• Iwi authority in resource management</li>
                <li>• Co-governance arrangements</li>
                <li>• Constitutional reform discussions</li>
              </ul>
            </div>

            <div className="card">
              <BookOpen className="h-8 w-8 text-ocean-teal mb-4" />
              <h3 className="text-xl font-semibold mb-3 text-gray-800">Cultural Sovereignty</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• Te Reo Māori revitalization</li>
                <li>• Tikanga-based justice systems</li>
                <li>• Cultural protocol in institutions</li>
                <li>• Protection of intellectual property</li>
              </ul>
            </div>

            <div className="card">
              <Heart className="h-8 w-8 text-ocean-teal mb-4" />
              <h3 className="text-xl font-semibold mb-3 text-gray-800">Economic Self-Reliance</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• Māori economic development</li>
                <li>• Resource ownership and management</li>
                <li>• Traditional economy practices</li>
                <li>• Financial independence strategies</li>
              </ul>
            </div>

            <div className="card">
              <Users className="h-8 w-8 text-ocean-teal mb-4" />
              <h3 className="text-xl font-semibold mb-3 text-gray-800">Social Well-being</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• Whānau-centered health models</li>
                <li>• Māori education systems</li>
                <li>• Community development initiatives</li>
                <li>• Social service delivery</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-ocean-teal text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Supporting Tino Rangatiratanga
          </h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto opacity-90">
            Whether you're Māori or tauiwi (non-Māori), you can support the expression of 
            tino rangatiratanga through education, advocacy, and respectful partnership.
          </p>
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <div className="text-center">
              <h3 className="text-lg font-semibold mb-2">Learn</h3>
              <p className="text-ocean-blue text-opacity-80">Educate yourself about Māori history and rights</p>
            </div>
            <div className="text-center">
              <h3 className="text-lg font-semibold mb-2">Advocate</h3>
              <p className="text-ocean-blue text-opacity-80">Support policies that recognize Māori sovereignty</p>
            </div>
            <div className="text-center">
              <h3 className="text-lg font-semibold mb-2">Partner</h3>
              <p className="text-ocean-blue text-opacity-80">Work alongside Māori in respectful partnership</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Tinorangatiratanga;
